// CardDetail: Daten-Hülle des Karten-Modals. Lädt den Umschlag, mappt `context` auf die
// Aktionen (Sammlung: zu Deck hinzufügen; Editor/Wantlist: Print wechseln/freigeben), meldet
// Ergebnisse als Toast und navigiert zu Binder/Deck. Die Darstellung ist ui/CardDetailModal.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import CardDetailModal from '../ui/CardDetailModal';
import { useToast } from '../ui/Toast';
import { json } from '../api/apiClient';
import { errorMessage } from '../api/errorMessage';
import { useCardDetail, useCardPrints, CARD_PRINTS_MAX_QUERY_LENGTH } from '../hooks/useCardDetail';

export default function CardDetail({
  open, onOpenChange, oracleId, scryfallId, fallback, section = 'card', context = null,
  canAct = true, printLocked = false, onSwitchPrint, onUnlockPrint, thumbnailOpensViewer = true,
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const detailQuery = useCardDetail(oracleId, scryfallId, open);

  // Suchbegriff der Print-Vollansicht: sofort im Feld, entprellt an den Server; beim Schließen leer
  const [printQuery, setPrintQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(printQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [printQuery]);
  useEffect(() => {
    if (!open) { setPrintQuery(''); setDebouncedQuery(''); }
  }, [open]);
  // Eine andere Karte erbt den Suchbegriff der vorherigen nicht. Zurückgesetzt wird schon im
  // Render (nicht erst im Effekt), sonst ginge für die neue Karte noch eine Abfrage mit dem alten
  // Suchbegriff hinaus.
  const [queryOracleId, setQueryOracleId] = useState(oracleId);
  if (queryOracleId !== oracleId) {
    setQueryOracleId(oracleId);
    setPrintQuery('');
    setDebouncedQuery('');
  }

  const printsQuery = useCardPrints(oracleId, debouncedQuery, open);
  const { data: printData, hasNextPage, isFetchingNextPage, fetchNextPage, refetch: refetchPrints } = printsQuery;
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const printsList = useMemo(() => {
    const pages = printData?.pages ?? [];
    return {
      items: pages.flatMap((p) => p.items),
      total: pages.length ? pages[0].total : null,
      hasMore: !!hasNextPage,
      loading: printsQuery.isLoading,
      loadingMore: isFetchingNextPage,
      error: printsQuery.error ?? null,
      loadMore,
      // Fehlte schon die erste Seite, wird neu geladen; sonst nur die fehlgeschlagene nächste
      onRetry: () => (pages.length ? fetchNextPage() : refetchPrints()),
      query: printQuery,
      // Leeren wirkt sofort (Verlassen der Vollansicht): sonst zeigt „Alle Prints“ noch 300 ms
      // die Treffer des alten Suchbegriffs. Gekürzt wird schon das Feld — FilterStrip.Search
      // kennt kein maxLength, und der Endpoint lehnt längere Begriffe ab.
      onQueryChange: (q) => {
        const next = q.slice(0, CARD_PRINTS_MAX_QUERY_LENGTH);
        setPrintQuery(next);
        if (!next.trim()) setDebouncedQuery('');
      },
    };
  }, [printData, hasNextPage, isFetchingNextPage, printsQuery.isLoading, printsQuery.error, loadMore, fetchNextPage, refetchPrints, printQuery]);

  // Zu Deck hinzufügen: virtuelle Decks des Nutzers — schon beim Öffnen geladen, damit der Picker ohne Wartezeit steht
  const [decks, setDecks] = useState(null);
  const [decksError, setDecksError] = useState(null);
  const loadDecks = useCallback(async () => {
    setDecksError(null);
    try {
      const data = await json('/api/decks/list/virtual');
      setDecks(data.decks || []);
    } catch (err) {
      setDecksError(err);
    }
  }, []);
  useEffect(() => {
    if (open && context === 'add-to-deck' && decks === null && !decksError) loadDecks();
    if (!open) { setDecks(null); setDecksError(null); }
  }, [open, context, decks, decksError, loadDecks]);

  const detail = detailQuery.data ?? null;
  const containingDeckIds = (detail?.virtualDecks ?? []).map((d) => `virtual_${d.deckId}`);

  // Ins Deck kommt der Print, den das Modal links zeigt; ohne gezeigten Print der des Aufrufers.
  const confirmAdd = async (deckId, shownScryfallId) => {
    try {
      const result = await json(`/api/decks/${deckId}/cards/add-by-scryfall-id`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scryfallId: shownScryfallId ?? scryfallId }),
      });
      if (result?.card) {
        toast.push({ tone: 'success', message: `„${result.card.name}" zu „${result.deckName}" hinzugefügt` });
      } else {
        toast.push({ tone: 'info', message: `${fallback?.name ?? 'Karte'} ist bereits im Deck` });
      }
      queryClient.invalidateQueries({ queryKey: ['card-detail', oracleId] });
      queryClient.invalidateQueries({ queryKey: ['deck'] });
      queryClient.invalidateQueries({ queryKey: ['deck-editor'] });
      queryClient.invalidateQueries({ queryKey: ['decks'] });
      return true;
    } catch (err) {
      toast.push({ tone: 'error', message: errorMessage(err, 'Karte konnte nicht zum Deck hinzugefügt werden.') });
      return false;
    }
  };

  const switchPrint = async (newScryfallId) => {
    if (!onSwitchPrint) return false;
    const ok = await onSwitchPrint(newScryfallId);
    if (ok) queryClient.invalidateQueries({ queryKey: ['card-detail', oracleId] });
    return ok;
  };

  return (
    <CardDetailModal
      open={open}
      onOpenChange={onOpenChange}
      fallback={fallback}
      detail={detail}
      loading={detailQuery.isLoading}
      error={detailQuery.error ?? null}
      onRetry={() => detailQuery.refetch()}
      currentScryfallId={scryfallId}
      section={section}
      context={context}
      canAct={canAct}
      printLocked={printLocked}
      onUnlockPrint={onUnlockPrint}
      onSwitchPrint={switchPrint}
      addToDeck={context === 'add-to-deck' ? {
        decks, error: decksError, onRetry: loadDecks, containingDeckIds, onConfirm: confirmAdd,
        emptyAction: <button type="button" className="btn-primary" onClick={() => { onOpenChange(false); navigate('/decks'); }}>Zu den Decks</button>,
      } : undefined}
      printsList={printsList}
      onOpenDeck={(deckId) => { onOpenChange(false); navigate(`/decks/virtual_${deckId}/editor`); }}
      thumbnailOpensViewer={thumbnailOpensViewer}
    />
  );
}
